import { CommandDefinition, CommandResult, CommandContext } from '../../contexts/CommandContext';

export interface CommandCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  commands: CommandDefinition[];
}

export interface CommandHelp {
  command: string;
  description: string;
  usage: string;
  examples: string[];
  category: string;
  permissions: string[];
}

// Context dependencies that will be injected
export interface CommandDependencies {
  artifacts: any[];
  processes: any[];
  states: any[];
  transitions: any[];
  fetchArtifacts: (env: string) => Promise<any[]>;
  fetchProcesses: (env: string) => Promise<any[]>;
  fetchStates: (env: string) => Promise<any[]>;
  threads?: any[];
  createThread?: (name: string, description?: string) => Promise<any>;
  switchThread?: (threadId: string) => void;
  deleteThread?: (threadId: string) => Promise<void>;
  pinnedArtifacts?: any[];
  pinArtifact?: (artifactId: string) => Promise<void>;
  unpinArtifact?: (artifactId: string) => Promise<void>;
}

export class CommandRegistry {
  private static instance: CommandRegistry;
  private commands: Map<string, CommandDefinition> = new Map();
  private categories: Map<string, CommandCategory> = new Map();
  private dependencies: CommandDependencies | null = null;

  private constructor() {
    this.initializeCommands();
  }
  
  public setDependencies(deps: CommandDependencies): void {
    this.dependencies = deps;
  }

  public static getInstance(): CommandRegistry {
    if (!CommandRegistry.instance) {
      CommandRegistry.instance = new CommandRegistry();
    }
    return CommandRegistry.instance;
  }

  private initializeCommands() {
    // Navigation Commands
    this.registerCommand({
      name: 'help',
      description: 'Show available commands and help information',
      usage: '/help [command]',
      examples: ['/help', '/help add', '/help find'],
      category: 'system',
      permissions: [],
      handler: this.handleHelp.bind(this),
    });

    this.registerCommand({
      name: 'clear',
      description: 'Clear the current chat',
      usage: '/clear',
      examples: ['/clear'],
      category: 'system',
      permissions: [],
      handler: this.handleClear.bind(this),
    });

    // Thread Management Commands
    this.registerCommand({
      name: 'thread',
      description: 'Create, switch, or manage chat threads',
      usage: '/thread [create|switch|list|delete] [name]',
      examples: ['/thread create "New Project"', '/thread switch "Project A"', '/thread list'],
      category: 'system',
      permissions: ['read:threads'],
      handler: this.handleThread.bind(this),
    });

    this.registerCommand({
      name: 'pin',
      description: 'Pin or unpin artifacts to the sidebar',
      usage: '/pin [add|remove|list] [artifact_name]',
      examples: ['/pin add "Operating Checking"', '/pin remove "Invoice"', '/pin list'],
      category: 'navigation',
      permissions: ['read:artifacts'],
      handler: this.handlePin.bind(this),
    });

    // Artifact Management Commands
    this.registerCommand({
      name: 'add',
      description: 'Add new artifacts, processes, or states',
      usage: '/add [artifact|process|state] [name] [options]',
      examples: ['/add artifact "New Account"', '/add process "Payment Processing"', '/add state "Active"'],
      category: 'data',
      permissions: ['write:artifacts'],
      handler: this.handleAdd.bind(this),
    });

    this.registerCommand({
      name: 'find',
      description: 'Find artifacts, processes, or states',
      usage: '/find [artifact|process|state] [name]',
      examples: ['/find artifact "Account"', '/find process "Payment"', '/find state "Active"'],
      category: 'data',
      permissions: ['read:artifacts'],
      handler: this.handleFind.bind(this),
    });

    this.registerCommand({
      name: 'list',
      description: 'List artifacts, processes, or states',
      usage: '/list [artifacts|processes|states] [filter]',
      examples: ['/list artifacts', '/list processes active', '/list states'],
      category: 'data',
      permissions: ['read:artifacts'],
      handler: this.handleList.bind(this),
    });

    this.registerCommand({
      name: 'show',
      description: 'Show detailed view of an artifact',
      usage: '/show [artifact_name]',
      examples: ['/show "Operating Checking"', '/show "Invoice Processing"'],
      category: 'data',
      permissions: ['read:artifacts'],
      handler: this.handleShow.bind(this),
    });

    // Data Commands
    this.registerCommand({
      name: 'items',
      description: 'Show items for a specific artifact',
      usage: '/items <artifact_name>',
      examples: ['/items "Operating Checking"', '/items "Invoice Processing"'],
      category: 'data',
      permissions: ['read:artifacts'],
      handler: this.handleItems.bind(this),
    });

    this.registerCommand({
      name: 'map',
      description: 'Display state map for an artifact type',
      usage: '/map <artifact_type>',
      examples: ['/map financial_account', '/map process'],
      category: 'data',
      permissions: ['read:artifacts'],
      handler: this.handleMap.bind(this),
    });

    this.registerCommand({
      name: 'process',
      description: 'Show detailed process information',
      usage: '/process <process_name>',
      examples: ['/process "Treasury: Reconcile Accounts"', '/process "Invoice Processing"'],
      category: 'data',
      permissions: ['read:processes'],
      handler: this.handleProcess.bind(this),
    });

    // Validation Commands
    this.registerCommand({
      name: 'validate',
      description: 'Run validation checks',
      usage: '/validate [scope]',
      examples: ['/validate', '/validate artifacts', '/validate processes'],
      category: 'analysis',
      permissions: ['read:artifacts', 'read:processes'],
      handler: this.handleValidate.bind(this),
    });

    this.registerCommand({
      name: 'check',
      description: 'Check specific conditions or constraints',
      usage: '/check [condition]',
      examples: ['/check dependencies', '/check states', '/check processes'],
      category: 'analysis',
      permissions: ['read:artifacts'],
      handler: this.handleCheck.bind(this),
    });

    // AI Commands
    this.registerCommand({
      name: 'ask',
      description: 'Ask the AI assistant a question',
      usage: '/ask <question>',
      examples: ['/ask "How do I add a new state?"', '/ask "What processes are missing?"'],
      category: 'ai',
      permissions: [],
      handler: this.handleAsk.bind(this),
    });

    this.registerCommand({
      name: 'suggest',
      description: 'Get AI suggestions for improvements',
      usage: '/suggest [type]',
      examples: ['/suggest processes', '/suggest states', '/suggest improvements'],
      category: 'ai',
      permissions: [],
      handler: this.handleSuggest.bind(this),
    });

    // Prime Model Commands
    this.registerCommand({
      name: 'prime-artifact',
      description: 'Display a Prime Model artifact with development tracking',
      usage: '/prime-artifact <artifact_name>',
      examples: ['/prime-artifact "Business Entity"', '/prime-artifact "Product"'],
      category: 'data',
      permissions: ['read:prime'],
      handler: this.handlePrimeArtifact.bind(this),
    });

    this.registerCommand({
      name: 'prime-status',
      description: 'Show development status for a Prime Model artifact',
      usage: '/prime-status <artifact_name>',
      examples: ['/prime-status "Business Entity"', '/prime-status "Invoice"'],
      category: 'data',
      permissions: ['read:prime'],
      handler: this.handlePrimeStatus.bind(this),
    });

    this.registerCommand({
      name: 'prime-states',
      description: 'Display states for a Prime Model artifact',
      usage: '/prime-states <artifact_name>',
      examples: ['/prime-states "Business Entity"', '/prime-states "Product"'],
      category: 'data',
      permissions: ['read:prime'],
      handler: this.handlePrimeStates.bind(this),
    });

    this.registerCommand({
      name: 'prime-transitions',
      description: 'Display transitions for a Prime Model artifact',
      usage: '/prime-transitions <artifact_name>',
      examples: ['/prime-transitions "Business Entity"', '/prime-transitions "Product"'],
      category: 'data',
      permissions: ['read:prime'],
      handler: this.handlePrimeTransitions.bind(this),
    });

    this.registerCommand({
      name: 'prime-processes',
      description: 'Display processes for a Prime Model artifact',
      usage: '/prime-processes <artifact_name>',
      examples: ['/prime-processes "Business Entity"', '/prime-processes "Product"'],
      category: 'data',
      permissions: ['read:prime'],
      handler: this.handlePrimeProcesses.bind(this),
    });

    // Initialize categories
    this.initializeCategories();
  }

  private initializeCategories() {
    const categories: CommandCategory[] = [
      {
        id: 'system',
        name: 'System',
        description: 'System and utility commands',
        icon: '⚙️',
        commands: this.getCommandsByCategory('system'),
      },
      {
        id: 'thread',
        name: 'Threads',
        description: 'Chat thread management',
        icon: '💬',
        commands: this.getCommandsByCategory('thread'),
      },
      {
        id: 'navigation',
        name: 'Navigation',
        description: 'Navigation and pinning',
        icon: '🧭',
        commands: this.getCommandsByCategory('navigation'),
      },
      {
        id: 'create',
        name: 'Create',
        description: 'Create new elements',
        icon: '➕',
        commands: this.getCommandsByCategory('create'),
      },
      {
        id: 'search',
        name: 'Search',
        description: 'Find and list elements',
        icon: '🔍',
        commands: this.getCommandsByCategory('search'),
      },
      {
        id: 'data',
        name: 'Data',
        description: 'View data and information',
        icon: '📊',
        commands: this.getCommandsByCategory('data'),
      },
      {
        id: 'analysis',
        name: 'Analysis',
        description: 'Validation and analysis',
        icon: '🔬',
        commands: this.getCommandsByCategory('analysis'),
      },
      {
        id: 'ai',
        name: 'AI Assistant',
        description: 'AI-powered assistance',
        icon: '🤖',
        commands: this.getCommandsByCategory('ai'),
      },
    ];

    categories.forEach(category => {
      this.categories.set(category.id, category);
    });
  }

  public registerCommand(command: CommandDefinition): void {
    this.commands.set(command.name, command);
  }

  public getCommand(name: string): CommandDefinition | undefined {
    return this.commands.get(name);
  }

  public getAllCommands(): CommandDefinition[] {
    return Array.from(this.commands.values());
  }

  public getCommandsByCategory(category: string): CommandDefinition[] {
    return this.getAllCommands().filter(cmd => cmd.category === category);
  }

  public getCategories(): CommandCategory[] {
    return Array.from(this.categories.values());
  }

  public getCommandHelp(commandName?: string): CommandHelp[] {
    if (commandName) {
      const command = this.getCommand(commandName);
      if (command) {
        return [{
          command: command.name,
          description: command.description,
          usage: command.usage,
          examples: command.examples,
          category: command.category,
          permissions: command.permissions,
        }];
      }
      return [];
    }

    return this.getAllCommands().map(cmd => ({
      command: cmd.name,
      description: cmd.description,
      usage: cmd.usage,
      examples: cmd.examples,
      category: cmd.category,
      permissions: cmd.permissions,
    }));
  }

  public searchCommands(query: string): CommandDefinition[] {
    const lowerQuery = query.toLowerCase();
    return this.getAllCommands().filter(cmd => 
      cmd.name.toLowerCase().includes(lowerQuery) ||
      cmd.description.toLowerCase().includes(lowerQuery) ||
      cmd.category.toLowerCase().includes(lowerQuery)
    );
  }

  // Command Handlers
  private async handleHelp(args: string[], _context: CommandContext): Promise<CommandResult> {
    const commandName = args[0];
    
    if (commandName) {
      const help = this.getCommandHelp(commandName);
      if (help.length > 0) {
        const cmd = help[0];
        
        // Format detailed help message
        let message = `📖 **Help for \`/${cmd.command}\`**\n\n`;
        message += `**Description**: ${cmd.description}\n\n`;
        message += `**Usage**: \`${cmd.usage}\`\n\n`;
        
        if (cmd.examples && cmd.examples.length > 0) {
          message += `**Examples**:\n`;
          cmd.examples.forEach(example => {
            message += `  • \`${example}\`\n`;
          });
          message += '\n';
        }
        
        message += `**Category**: ${cmd.category}\n`;
        if (cmd.permissions && cmd.permissions.length > 0) {
          message += `**Permissions**: ${cmd.permissions.join(', ')}\n`;
        }
        
        return {
          success: true,
          message: message,
          data: {
            command: cmd.command,
            description: cmd.description,
            usage: cmd.usage,
            examples: cmd.examples,
            category: cmd.category,
            permissions: cmd.permissions,
          },
        };
      } else {
        return {
          success: false,
          message: `❌ Command '${commandName}' not found`,
          error: `Unknown command: ${commandName}`,
        };
      }
    }

    // Show all commands grouped by category
    const categories = this.getCategories();
    
    // Format commands into a readable message with better layout
    let message = '📋 **Available Commands**\n\n';
    
    categories.forEach(category => {
      if (category.commands.length > 0) {
        message += `## ${category.icon} ${category.name}\n`;
        message += `*${category.description}*\n\n`;
        
        // Format commands in a simpler single-column table
        message += '| Command | Description |\n';
        message += '|---------|-------------|\n';
        
        category.commands.forEach(cmd => {
          message += `| \`/${cmd.name}\` | ${cmd.description} |\n`;
        });
        
        message += '\n';
      }
    });
    
    message += '---\n\n';
    message += '💡 **Usage**: Type `/help [command]` for detailed help on a specific command\n';
    message += '💡 **Examples**: `/help add`, `/help find`, `/help list`\n';
    message += '💡 **Quick Commands**: `/clear`, `/thread list`, `/find artifact`';
    
    return {
      success: true,
      message: message,
      data: {
        categories: categories.map(cat => ({
          name: cat.name,
          description: cat.description,
          icon: cat.icon,
          commands: cat.commands.map(cmd => ({
            name: cmd.name,
            description: cmd.description,
            usage: cmd.usage,
          })),
        })),
      },
    };
  }

  private async handleClear(_args: string[], _context: CommandContext): Promise<CommandResult> {
    return {
      success: true,
      message: '🧹 Chat cleared successfully!',
      data: { action: 'clear' },
    };
  }

  private async handleThread(args: string[], _context: CommandContext): Promise<CommandResult> {
    const action = args[0];
    const name = args.slice(1).join(' ');

    if (!this.dependencies) {
      return {
        success: false,
        message: 'Command system not fully initialized',
        error: 'Dependencies not available',
      };
    }

    try {
      switch (action?.toLowerCase()) {
        case 'create': {
          if (!name) {
            return {
              success: false,
              message: 'Thread name is required',
              error: 'Usage: /thread create "Thread Name"',
            };
          }
          
          if (this.dependencies.createThread) {
            const newThread = await this.dependencies.createThread(name, `Created via /thread command`);
            return {
              success: true,
              message: `✅ Created new thread: **${name}**`,
              data: { action: 'create_thread', name, threadId: newThread.id },
              suggestions: [`/thread switch ${name}`, '/thread list'],
            };
          }
          
          return {
            success: false,
            message: 'Thread creation not available',
            error: 'Thread management function not initialized',
          };
        }

        case 'switch': {
          if (!name) {
            return {
              success: false,
              message: 'Thread name or ID is required',
              error: 'Usage: /thread switch "Thread Name"',
            };
          }
          
          const threads = this.dependencies.threads || [];
          const thread = threads.find((t: any) => 
            t.name?.toLowerCase().includes(name.toLowerCase()) || 
            t.id === name
          );
          
          if (!thread) {
            return {
              success: false,
              message: `Thread not found: "${name}"`,
              error: `No thread matching "${name}"`,
              suggestions: ['/thread list'],
            };
          }
          
          if (this.dependencies.switchThread) {
            this.dependencies.switchThread(thread.id);
            return {
              success: true,
              message: `✅ Switched to thread: **${thread.name}**`,
              data: { action: 'switch_thread', name: thread.name, threadId: thread.id },
            };
          }
          
          return {
            success: false,
            message: 'Thread switching not available',
            error: 'Thread management function not initialized',
          };
        }

        case 'list': {
          const threads = this.dependencies.threads || [];
          
          if (threads.length === 0) {
            return {
              success: true,
              message: '📋 No threads available.\n\nCreate a new thread with `/thread create "Thread Name"`',
              data: { action: 'list_threads', threads: [] },
              suggestions: ['/thread create "New Thread"'],
            };
          }
          
          const message = `📋 **Available Threads** (${threads.length} total)\n\n` +
            threads.map((thread: any, idx: number) => {
              const name = thread.name || `Thread ${idx + 1}`;
              const messageCount = thread.messageCount || thread.messages?.length || 0;
              const isPinned = thread.isPinned ? '📌 ' : '';
              const isActive = thread.isActive ? '🟢 ' : '';
              return `${idx + 1}. ${isPinned}${isActive}**${name}** (${messageCount} messages)`;
            }).join('\n');
          
          return {
            success: true,
            message,
            data: { action: 'list_threads', threads, total: threads.length },
            suggestions: ['/thread create', '/thread switch'],
          };
        }

        case 'delete': {
          if (!name) {
            return {
              success: false,
              message: 'Thread name or ID is required',
              error: 'Usage: /thread delete "Thread Name"',
            };
          }
          
          const threads = this.dependencies.threads || [];
          const thread = threads.find((t: any) => 
            t.name?.toLowerCase().includes(name.toLowerCase()) || 
            t.id === name
          );
          
          if (!thread) {
            return {
              success: false,
              message: `Thread not found: "${name}"`,
              error: `No thread matching "${name}"`,
              suggestions: ['/thread list'],
            };
          }
          
          if (this.dependencies.deleteThread) {
            await this.dependencies.deleteThread(thread.id);
            return {
              success: true,
              message: `✅ Deleted thread: **${thread.name}**`,
              data: { action: 'delete_thread', name: thread.name, threadId: thread.id },
              suggestions: ['/thread list'],
            };
          }
          
          return {
            success: false,
            message: 'Thread deletion not available',
            error: 'Thread management function not initialized',
          };
        }

        default:
          return {
            success: false,
            message: 'Invalid thread action',
            error: 'Usage: /thread [create|switch|list|delete] [name]',
            suggestions: ['/thread list', '/thread create "New Thread"'],
          };
      }
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to execute thread command',
        error: error.message,
      };
    }
  }

  private async handlePin(args: string[], _context: CommandContext): Promise<CommandResult> {
    const action = args[0];
    const artifactQuery = args.slice(1).join(' ').toLowerCase();

    if (!this.dependencies) {
      return {
        success: false,
        message: 'Command system not fully initialized',
        error: 'Dependencies not available',
      };
    }

    try {
      switch (action?.toLowerCase()) {
        case 'add': {
          if (!artifactQuery) {
            return {
              success: false,
              message: 'Artifact name is required',
              error: 'Usage: /pin add "Artifact Name"',
              suggestions: ['/list artifacts', '/pin list'],
            };
          }
          
          const artifacts = this.dependencies.artifacts || [];
          const artifact = artifacts.find((a: any) => 
            a.name?.toLowerCase().includes(artifactQuery) || 
            a.id?.toString().toLowerCase() === artifactQuery
          );
          
          if (!artifact) {
            return {
              success: false,
              message: `Artifact not found: "${artifactQuery}"`,
              error: `No artifact matching "${artifactQuery}"`,
              suggestions: ['/list artifacts', `/find artifact ${artifactQuery}`],
            };
          }
          
          if (this.dependencies.pinArtifact) {
            await this.dependencies.pinArtifact(artifact.id);
            return {
              success: true,
              message: `📌 Pinned artifact: **${artifact.name}**`,
              data: { action: 'pin_artifact', name: artifact.name, artifactId: artifact.id },
              suggestions: ['/pin list', `/items ${artifact.name}`],
            };
          }
          
          return {
            success: false,
            message: 'Pin functionality not available',
            error: 'Pin management function not initialized',
          };
        }

        case 'remove':
        case 'unpin': {
          if (!artifactQuery) {
            return {
              success: false,
              message: 'Artifact name is required',
              error: 'Usage: /pin remove "Artifact Name"',
              suggestions: ['/pin list'],
            };
          }
          
          const pinnedArtifacts = this.dependencies.pinnedArtifacts || [];
          const artifact = pinnedArtifacts.find((a: any) => 
            a.name?.toLowerCase().includes(artifactQuery) || 
            a.id?.toString().toLowerCase() === artifactQuery
          );
          
          if (!artifact) {
            return {
              success: false,
              message: `Pinned artifact not found: "${artifactQuery}"`,
              error: `No pinned artifact matching "${artifactQuery}"`,
              suggestions: ['/pin list'],
            };
          }
          
          if (this.dependencies.unpinArtifact) {
            await this.dependencies.unpinArtifact(artifact.id);
            return {
              success: true,
              message: `📍 Unpinned artifact: **${artifact.name}**`,
              data: { action: 'unpin_artifact', name: artifact.name, artifactId: artifact.id },
              suggestions: ['/pin list'],
            };
          }
          
          return {
            success: false,
            message: 'Unpin functionality not available',
            error: 'Pin management function not initialized',
          };
        }

        case 'list': {
          const pinnedArtifacts = this.dependencies.pinnedArtifacts || [];
          
          if (pinnedArtifacts.length === 0) {
            return {
              success: true,
              message: '📌 No pinned artifacts.\n\nUse `/pin add "Artifact Name"` to pin an artifact.',
              data: { action: 'list_pinned', artifacts: [] },
              suggestions: ['/list artifacts', '/pin add'],
            };
          }
          
          const message = `📌 **Pinned Artifacts** (${pinnedArtifacts.length} total)\n\n` +
            pinnedArtifacts.map((artifact: any, idx: number) => {
              const name = artifact.name || `Artifact ${idx + 1}`;
              const type = artifact.type || artifact.artifact_type || '';
              const itemCount = artifact.items?.length || artifact.artifactItems?.length || 0;
              return `${idx + 1}. **${name}**${type ? ` (${type})` : ''} - ${itemCount} items`;
            }).join('\n');
          
          const cards = pinnedArtifacts.slice(0, 5).map((artifact: any) => ({
            type: 'artifact-detail',
            artifactId: artifact.id,
            artifact,
          }));
          
          return {
            success: true,
            message,
            data: { action: 'list_pinned', artifacts: pinnedArtifacts, total: pinnedArtifacts.length },
            cards,
            suggestions: ['/pin add', '/pin remove'],
          };
        }

        default:
          return {
            success: false,
            message: 'Invalid pin action',
            error: 'Usage: /pin [add|remove|list] [artifact_name]',
            suggestions: ['/pin list', '/pin add', '/list artifacts'],
          };
      }
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to execute pin command',
        error: error.message,
      };
    }
  }

  private async handleAdd(args: string[], _context: CommandContext): Promise<CommandResult> {
    const type = args[0];
    const name = args.slice(1).join(' ');

    if (!type || !name) {
      return {
        success: false,
        message: 'Type and name are required',
        error: 'Usage: /add [artifact|process|state] [name]',
      };
    }

    return {
      success: true,
      message: `Adding ${type}: ${name}`,
      data: { action: 'add_element', type, name },
    };
  }

  private async handleFind(args: string[], context: CommandContext): Promise<CommandResult> {
    const type = args[0];
    const searchQuery = args.slice(1).join(' ').toLowerCase();

    if (!type || !searchQuery) {
      return {
        success: false,
        message: 'Type and search query are required',
        error: 'Usage: /find [artifact|process|state] [search query]',
      };
    }

    if (!this.dependencies) {
      return {
        success: false,
        message: 'Command system not fully initialized',
        error: 'Dependencies not available',
      };
    }

    try {
      let items: any[] = [];
      let itemType = '';

      switch (type.toLowerCase()) {
        case 'artifact':
        case 'artifacts':
          items = this.dependencies.artifacts || [];
          itemType = 'artifact';
          break;
        case 'process':
        case 'processes':
          items = this.dependencies.processes || [];
          itemType = 'process';
          break;
        case 'state':
        case 'states':
          items = this.dependencies.states || [];
          itemType = 'state';
          break;
        default:
          return {
            success: false,
            message: `Unknown type: ${type}`,
            error: 'Valid types are: artifact, process, state',
          };
      }

      // Search through items
      const results = items.filter(item => {
        const name = item.name?.toLowerCase() || '';
        const type = item.type?.toLowerCase() || '';
        const description = item.description?.toLowerCase() || '';
        const id = item.id?.toString().toLowerCase() || '';
        
        return name.includes(searchQuery) || 
               type.includes(searchQuery) || 
               description.includes(searchQuery) ||
               id.includes(searchQuery);
      });

      // Context-sensitive filtering based on current thread's model
      let filteredResults = results;
      
      if (itemType === 'artifact' && context.currentThreadModel) {
        if (context.currentThreadModel.type === 'prime') {
          // For Prime model, show only prime artifacts and their children
          if (context.currentThreadModel.artifactId) {
            // If specific prime artifact is selected, show only its children
            filteredResults = results.filter(item => 
              item.prime_artifact_id === context.currentThreadModel?.artifactId ||
              item.parent_ref === context.currentThreadModel?.artifactId
            );
          } else {
            // Show all prime artifacts
            filteredResults = results.filter(item => 
              item.isPrime === true || 
              item.artifact_type === 'PrimeArtifact' ||
              item.prime_artifact_id !== null
            );
          }
        } else if (context.currentThreadModel.type === 'company') {
          // For Company model, show only company artifacts in the specific tree
          if (context.currentThreadModel.artifactTreeId) {
            // If specific artifact tree is selected, show only artifacts in that tree
            filteredResults = results.filter(item => 
              item.artifact_tree_id === context.currentThreadModel?.artifactTreeId ||
              item.isPrime !== true && item.artifact_type !== 'PrimeArtifact'
            );
          } else {
            // Show all company artifacts (non-prime)
            filteredResults = results.filter(item => 
              item.isPrime !== true && 
              item.artifact_type !== 'PrimeArtifact' &&
              item.prime_artifact_id === null
            );
          }
        }
      } else if (itemType === 'process' && context.currentThreadModel) {
        // For processes, filter based on the thread's artifact context
        if (context.currentThreadContext?.selectedArtifactId) {
          // Show only processes related to the selected artifact
          filteredResults = results.filter(item => 
            item.artifact_ref === context.currentThreadContext?.selectedArtifactId
          );
        }
      }

      if (filteredResults.length === 0) {
        const contextInfo = context.currentThreadModel 
          ? ` in ${context.currentThreadModel.type} model context`
          : ` in ${context.environment} environment`;
        
        return {
          success: true,
          message: `🔍 No ${itemType}s found matching "${searchQuery}"${contextInfo}`,
          data: { action: 'find_element', type: itemType, query: searchQuery, results: [] },
          suggestions: [`/list ${itemType}s`, `/add ${itemType}`],
        };
      }

      const contextInfo = context.currentThreadModel 
        ? ` in ${context.currentThreadModel.type} model context`
        : ` in ${context.environment} environment`;
      
      let message = `🔍 **Found ${filteredResults.length} ${itemType}(s) matching "${searchQuery}"${contextInfo}**\n\n`;
      
      if (filteredResults.length === 1) {
        // Single result - show full details
        const item = filteredResults[0];
        const name = item.name || item.title || 'Unnamed';
        const itemTypeStr = item.type || item.artifact_type || '';
        const status = item.status || item.state || '';
        const id = item.id || '';
        const description = item.description || '';
        
        message += `**${name}**\n`;
        message += `ID: \`${id}\`\n`;
        if (itemTypeStr) message += `Type: ${itemTypeStr}\n`;
        if (status) message += `Status: ${status}\n`;
        if (description) message += `\n${description}\n`;
        message += `\n💡 Use \`/items "${name}"\` to see item details.`;
      } else if (filteredResults.length <= 10) {
        // Small result set - show list
        message += filteredResults.map((item, idx) => {
          const name = item.name || item.title || 'Unnamed';
          const itemTypeStr = item.type || item.artifact_type || '';
          const id = item.id || '';
          return `${idx + 1}. **${name}** (\`${id}\`)${itemTypeStr ? ` - ${itemTypeStr}` : ''}`;
        }).join('\n');
        
        message += `\n\n💡 Use \`/items "<name>"\` to see specific ${itemType} details.`;
      } else {
        // Large result set - show summary
        message += `**Top 5 results:**\n`;
        message += filteredResults.slice(0, 5).map((item, idx) => {
          const name = item.name || item.title || 'Unnamed';
          const itemTypeStr = item.type || item.artifact_type || '';
          return `${idx + 1}. **${name}**${itemTypeStr ? ` (${itemTypeStr})` : ''}`;
        }).join('\n');
        
        message += `\n\n... and ${filteredResults.length - 5} more results.\n\n`;
        message += `💡 **Narrow your search**: \`/find ${itemType} <more specific term>\``;
      }

      // Only return card for single artifact result
      const cards = (itemType === 'artifact' && filteredResults.length === 1) ? [{
        type: 'artifact-detail',
        artifactId: filteredResults[0].id,
        artifact: filteredResults[0],
      }] : undefined;

      const suggestions: string[] = [];
      if (filteredResults.length === 1) {
        suggestions.push(`/items ${filteredResults[0].name}`);
      } else if (filteredResults.length > 0) {
        suggestions.push(`/find ${itemType} ${searchQuery} <refine>`);
      }
      suggestions.push(`/list ${itemType}s`);

      return {
        success: true,
        message,
        data: { 
          action: 'find_element', 
          type: itemType, 
          query: searchQuery,
          results: filteredResults.slice(0, 10),
          total: filteredResults.length,
        },
        cards,
        suggestions,
      };
    } catch (error: any) {
      return {
        success: false,
        message: `Failed to search for ${type}`,
        error: error.message,
      };
    }
  }

  private async handleList(args: string[], context: CommandContext): Promise<CommandResult> {
    const type = args[0];
    const filter = args.slice(1).join(' ').toLowerCase();

    console.log('📋 [CommandRegistry.handleList] Starting list command:', { type, filter, context });

    if (!type) {
      return {
        success: false,
        message: 'Type is required',
        error: 'Usage: /list [artifacts|processes|states] [filter]',
      };
    }

    if (!this.dependencies) {
      console.error('❌ [CommandRegistry.handleList] Dependencies not available!');
      return {
        success: false,
        message: 'Command system not fully initialized',
        error: 'Dependencies not available',
      };
    }

    console.log('📦 [CommandRegistry.handleList] Dependencies available:', {
      hasArtifacts: !!this.dependencies.artifacts,
      artifactCount: this.dependencies.artifacts?.length || 0,
      hasProcesses: !!this.dependencies.processes,
      processCount: this.dependencies.processes?.length || 0,
    });

    try {
      let items: any[] = [];
      let itemType = '';

      switch (type.toLowerCase()) {
        case 'artifacts':
        case 'artifact':
          items = this.dependencies.artifacts || [];
          itemType = 'artifacts';
          console.log('📊 [CommandRegistry.handleList] Loading artifacts:', items.length);
          break;
        case 'processes':
        case 'process':
          items = this.dependencies.processes || [];
          itemType = 'processes';
          console.log('📊 [CommandRegistry.handleList] Loading processes:', items.length);
          break;
        case 'states':
        case 'state':
          items = this.dependencies.states || [];
          itemType = 'states';
          console.log('📊 [CommandRegistry.handleList] Loading states:', items.length);
          break;
        default:
          return {
            success: false,
            message: `Unknown type: ${type}`,
            error: 'Valid types are: artifacts, processes, states',
          };
      }

      // Apply filter if provided
      if (filter) {
        items = items.filter(item => {
          const name = item.name?.toLowerCase() || '';
          const type = item.type?.toLowerCase() || '';
          const description = item.description?.toLowerCase() || '';
          return name.includes(filter) || type.includes(filter) || description.includes(filter);
        });
      }

      // Context-sensitive filtering based on current thread's model
      console.log('🔍 [CommandRegistry.handleList] Thread context analysis:');
      console.log('  - context.threadModelContext:', context.threadModelContext);
      
      // Use ONLY backend thread context - backend is now the single source of truth
      const threadContext = context.threadModelContext;
      console.log('  - Using backend thread context:', threadContext);
      
      if (itemType === 'artifacts' && threadContext) {
        console.log('🔍 [CommandRegistry.handleList] Artifact filtering debug:');
        console.log('  - Total artifacts before filtering:', items.length);
        
        // Show detailed structure of first few artifacts
        const sampleArtifacts = items.slice(0, 3);
        sampleArtifacts.forEach((artifact, index) => {
          console.log(`  - Artifact ${index + 1}:`, {
            id: artifact.id,
            name: artifact.name,
            isPrime: artifact.isPrime,
            artifact_type: artifact.artifact_type,
            prime_artifact_id: artifact.prime_artifact_id,
            parent_ref: artifact.parent_ref,
            artifact_tree_id: artifact.artifact_tree_id,
            allKeys: Object.keys(artifact)
          });
        });
        
        // Determine model type from thread context
        const modelType = (threadContext as any).model_type || (threadContext as any).type;
        if (modelType === 'prime') {
          console.log('🔍 [CommandRegistry.handleList] Applying PRIME model filtering');
          // For Prime model, show only prime artifacts and their children
          const primeArtifactId = (threadContext as any).prime_artifact_id || (threadContext as any).artifactId;
          if (primeArtifactId) {
            console.log('  - Filtering for specific prime artifact:', primeArtifactId);
            
            // Helper function to find all descendants of a given artifact
            const findAllDescendants = (rootId: string, allItems: any[]): any[] => {
              const descendants = new Set<string>();
              
              // Recursive function to traverse the hierarchy
              const traverse = (parentId: string) => {
                const children = allItems.filter(item => 
                  item.parent_ref === parentId || item.prime_artifact_id === parentId
                );
                
                children.forEach(child => {
                  descendants.add(child.id);
                  traverse(child.id); // Recursively find grandchildren
                });
              };
              
              traverse(rootId);
              return Array.from(descendants);
            };
            
            // Find the root artifact and all its descendants
            const rootArtifact = items.find(item => item.id === primeArtifactId);
            const descendantIds = findAllDescendants(primeArtifactId, items);
            
            console.log('  - Root artifact found:', rootArtifact ? rootArtifact.name : 'Not found');
            console.log('  - Descendant IDs found:', descendantIds.length, descendantIds);
            
            // Include the root artifact and all its descendants
            const allRelevantIds = [primeArtifactId, ...descendantIds];
            items = items.filter(item => allRelevantIds.includes(item.id));
            
            console.log('  - Artifacts after hierarchy filtering:', items.length);
          } else {
            console.log('  - Filtering for all prime artifacts');
            // Show all prime artifacts - let's be more inclusive for debugging
            items = items.filter(item => 
              item.isPrime === true || 
              item.artifact_type === 'PrimeArtifact' ||
              item.prime_artifact_id !== null ||
              item.prime_artifact_id !== undefined
            );
            console.log('  - Artifacts after general prime filtering:', items.length);
          }
        } else if (modelType === 'company') {
          console.log('🔍 [CommandRegistry.handleList] Applying COMPANY model filtering');
          // For Company model, show only company artifacts in the specific tree
          const artifactTreeId = (threadContext as any).artifact_tree_id || (threadContext as any).artifactTreeId;
          if (artifactTreeId) {
            console.log('  - Filtering for specific artifact tree:', artifactTreeId);
            // If specific artifact tree is selected, show only artifacts in that tree
            items = items.filter(item => 
              item.artifact_tree_id === artifactTreeId
            );
            console.log('  - Artifacts after specific tree filtering:', items.length);
          } else {
            console.log('  - Filtering for all company artifacts (non-prime)');
            // Show all company artifacts (non-prime) - be more inclusive for debugging
            items = items.filter(item => 
              // Include items that are clearly not prime artifacts
              item.isPrime === false || 
              item.isPrime === undefined ||
              item.artifact_type === 'CompanyArtifact' ||
              item.artifact_type === undefined ||
              // Exclude items that are clearly prime artifacts
              (item.isPrime !== true && 
               item.artifact_type !== 'PrimeArtifact' &&
               item.prime_artifact_id !== null)
            );
            console.log('  - Artifacts after general company filtering:', items.length);
          }
        }
        console.log(`📊 [CommandRegistry.handleList] Thread context filtering: showing ${items.length} ${itemType} in ${modelType} model`);
      } else {
        console.log('⚠️ [CommandRegistry.handleList] No thread context filtering applied:');
        console.log('  - itemType:', itemType);
        console.log('  - context.threadModelContext exists:', !!context.threadModelContext);
        console.log('  - Using default filtering (showing all items)');
      }
      
      if (itemType === 'processes' && threadContext) {
        // For processes, filter based on the thread's artifact context
        // Use threadModelContext to get the relevant artifact ID
        if (threadContext.model_type === 'prime' && threadContext.prime_artifact_id) {
          // Show only processes related to the prime artifact
          items = items.filter(item => 
            item.artifact_ref === threadContext.prime_artifact_id
          );
          console.log(`📊 [CommandRegistry.handleList] Thread context filtering: showing ${items.length} processes for prime artifact ${threadContext.prime_artifact_id}`);
        } else if (threadContext.model_type === 'company' && threadContext.artifact_tree_id) {
          // Show only processes related to artifacts in the company tree
          items = items.filter(item => 
            item.artifact_tree_id === threadContext.artifact_tree_id
          );
          console.log(`📊 [CommandRegistry.handleList] Thread context filtering: showing ${items.length} processes for company tree ${threadContext.artifact_tree_id}`);
        }
      }

      // For large lists, just show summary and filtering options
      let message = `📊 **Found ${items.length} ${itemType}**${filter ? ` matching "${filter}"` : ''}\n\n`;
      
      if (items.length === 0) {
        message += `No ${itemType} found. Try:\n`;
        message += `• Use \`/add ${itemType.slice(0, -1)}\` to create one\n`;
        message += `• Check your filter criteria\n`;
        message += `• Switch environments (Prime vs Company)`;
      } else if (items.length <= 10) {
        // Small list - show all with details
        message += items.map((item, idx) => {
          const name = item.name || item.title || 'Unnamed';
          const type = item.type || item.artifact_type || '';
          const status = item.status || item.state || '';
          const id = item.id || '';
          return `${idx + 1}. **${name}**${type ? ` (${type})` : ''}${status ? ` - ${status}` : ''}\nID: \`${id}\``;
        }).join('\n\n');
        
        message += `\n\n💡 **Tip**: Use \`/items "<name>"\` or \`/find ${itemType.slice(0, -1)} <name>\` to see details.`;
      } else {
        // Large list - show summary only with filtering guidance
        message += `**Filter options:**\n`;
        message += `• \`/list ${itemType} <search>\` - filter by name\n`;
        message += `• \`/find ${itemType.slice(0, -1)} <search>\` - detailed search\n\n`;
        
        message += `**Sample results** (showing first 5):\n`;
        message += items.slice(0, 5).map((item, idx) => {
          const name = item.name || item.title || 'Unnamed';
          const type = item.type || item.artifact_type || '';
          return `${idx + 1}. **${name}**${type ? ` (${type})` : ''}`;
        }).join('\n');
        
        message += `\n\n... and ${items.length - 5} more.\n\n`;
        message += `💡 **Next steps**: Use \`/find ${itemType.slice(0, -1)} <name>\` to search for a specific ${itemType.slice(0, -1)}.`;
      }

      // Don't return cards automatically - let user request them
      const suggestions: string[] = [];
      if (items.length > 0) {
        suggestions.push(`/find ${itemType} ${items[0]?.name || ''}`);
      }
      if (!filter) {
        suggestions.push(`/list ${itemType} <search>`);
      }
      suggestions.push(`/add ${itemType.slice(0, -1)}`);

      // For artifact lists, return the data structure the chat expects
      if (itemType === 'artifacts') {
        return {
          success: true,
          message,
          data: { 
            action: 'list_elements', 
            type: 'artifact_list',
            artifacts: items, // Return all artifacts for the list
            filter,
            total: items.length,
          },
          suggestions,
        };
      }

      return {
        success: true,
        message,
        data: { 
          action: 'list_elements', 
          type: itemType, 
          filter,
          items: items.slice(0, 10), // Only return top 10 in data for non-artifact lists
          total: items.length,
        },
        // No cards - user should use /find or /items to get cards
        suggestions,
      };
    } catch (error: any) {
      return {
        success: false,
        message: `Failed to list ${type}`,
        error: error.message,
      };
    }
  }

  private async handleItems(args: string[], _context: CommandContext): Promise<CommandResult> {
    const artifactQuery = args.join(' ').toLowerCase();

    if (!artifactQuery) {
      return {
        success: false,
        message: 'Artifact name is required',
        error: 'Usage: /items <artifact_name>',
        suggestions: ['/list artifacts'],
      };
    }

    if (!this.dependencies) {
      return {
        success: false,
        message: 'Command system not fully initialized',
        error: 'Dependencies not available',
      };
    }

    try {
      const artifacts = this.dependencies.artifacts || [];
      
      // Find matching artifact
      const artifact = artifacts.find(a => 
        a.name?.toLowerCase().includes(artifactQuery) || 
        a.id?.toString().toLowerCase() === artifactQuery
      );

      if (!artifact) {
        return {
          success: false,
          message: `❌ Artifact not found: "${artifactQuery}"\n\nTry:\n• \`/list artifacts\` to see all artifacts\n• \`/find artifact ${artifactQuery}\` for a broader search`,
          error: `No artifact matching "${artifactQuery}"`,
          data: { action: 'show_items', artifactName: artifactQuery, found: false },
          suggestions: ['/list artifacts', `/find artifact ${artifactQuery}`],
        };
      }

      // Get items from artifact
      const items = artifact.items || artifact.artifactItems || [];
      
      if (items.length === 0) {
        return {
          success: true,
          message: `📋 **${artifact.name}** has no items yet.\n\nUse \`/add item\` to add items to this artifact.`,
          data: { 
            action: 'show_items', 
            artifactName: artifact.name,
            artifactId: artifact.id,
            items: [],
          },
          suggestions: ['/add item', `/find artifact ${artifact.name}`],
        };
      }

      const message = `📋 **Items for ${artifact.name}** (${items.length} total)\n\n` +
        items.slice(0, 15).map((item: any, idx: number) => {
          const name = item.name || item.title || `Item ${idx + 1}`;
          const status = item.status || item.state || '';
          const value = item.value !== undefined ? ` - Value: ${item.value}` : '';
          return `${idx + 1}. **${name}**${status ? ` (${status})` : ''}${value}`;
        }).join('\n') +
        (items.length > 15 ? `\n\n... and ${items.length - 15} more items` : '');

      return {
        success: true,
        message,
        data: { 
          action: 'show_items', 
          artifactName: artifact.name,
          artifactId: artifact.id,
          items: items.slice(0, 15),
          total: items.length,
        },
        cards: [{
          type: 'artifact-detail',
          artifactId: artifact.id,
          artifact,
        }],
        suggestions: [`/add item to ${artifact.name}`, `/find artifact ${artifact.name}`],
      };
    } catch (error: any) {
      return {
        success: false,
        message: `Failed to show items`,
        error: error.message,
      };
    }
  }

  private async handleMap(args: string[], _context: CommandContext): Promise<CommandResult> {
    const artifactType = args[0];

    if (!artifactType) {
      return {
        success: false,
        message: 'Artifact type is required',
        error: 'Usage: /map <artifact_type>',
      };
    }

    return {
      success: true,
      message: `Showing state map for: ${artifactType}`,
      data: { action: 'show_map', artifactType },
    };
  }

  private async handleProcess(args: string[], _context: CommandContext): Promise<CommandResult> {
    const processName = args.join(' ');

    if (!processName) {
      return {
        success: false,
        message: 'Process name is required',
        error: 'Usage: /process <process_name>',
      };
    }

    return {
      success: true,
      message: `Showing process: ${processName}`,
      data: { action: 'show_process', processName },
    };
  }

  private async handleValidate(args: string[], _context: CommandContext): Promise<CommandResult> {
    const scope = args[0] || 'all';

    return {
      success: true,
      message: `Running validation for: ${scope}`,
      data: { action: 'validate', scope },
    };
  }

  private async handleCheck(args: string[], _context: CommandContext): Promise<CommandResult> {
    const condition = args[0];

    if (!condition) {
      return {
        success: false,
        message: 'Condition is required',
        error: 'Usage: /check [condition]',
      };
    }

    return {
      success: true,
      message: `Checking: ${condition}`,
      data: { action: 'check', condition },
    };
  }

  private async handleAsk(args: string[], _context: CommandContext): Promise<CommandResult> {
    const question = args.join(' ');

    if (!question) {
      return {
        success: false,
        message: 'Question is required',
        error: 'Usage: /ask <question>',
      };
    }

    return {
      success: true,
      message: `AI Assistant: Processing your question...`,
      data: { action: 'ai_ask', question },
    };
  }

  private async handleSuggest(args: string[], _context: CommandContext): Promise<CommandResult> {
    const type = args[0] || 'general';

    return {
      success: true,
      message: `Getting AI suggestions for: ${type}`,
      data: { action: 'ai_suggest', type },
    };
  }

  private async handleShow(args: string[], context: CommandContext): Promise<CommandResult> {
    const artifactQuery = args.join(' ').trim();

    if (!artifactQuery) {
      return {
        success: false,
        message: 'Artifact name is required',
        error: 'Usage: /show "Artifact Name"',
        suggestions: ['/list artifacts', '/find artifact'],
      };
    }

    // Search for the artifact
    const artifacts = this.dependencies?.artifacts || [];
    const artifact = artifacts.find((a: any) => 
      a.name?.toLowerCase().includes(artifactQuery.toLowerCase()) || 
      a.id?.toString().toLowerCase() === artifactQuery.toLowerCase()
    );

    if (!artifact) {
      return {
        success: false,
        message: `Artifact not found: "${artifactQuery}"`,
        error: `No artifact matching "${artifactQuery}"`,
        suggestions: ['/list artifacts', `/find artifact ${artifactQuery}`],
        data: { action: 'show_artifact', artifactName: artifactQuery, found: false },
      };
    }

    // Transform artifact to match EnhancedArtifact interface
    const enhancedArtifact = {
      id: artifact.id,
      name: artifact.name,
      type: artifact.type || artifact.artifact_type,
      description: artifact.description,
      status: artifact.status,
      created_at: artifact.created_at,
      artifact_type_id: artifact.type || artifact.artifact_type,
      prime_artifact_id: artifact.prime_artifact_id || null,
      parent_ref: artifact.parent_ref || null,
      business_model_id: artifact.business_model_id || null,
      is_deleted: artifact.is_deleted || false,
      artifact_metadata: artifact.artifact_metadata || {},
      updated_at: artifact.updated_at || artifact.created_at,
      children: artifact.children || [],
      parameters: artifact.parameters || [],
      processes: artifact.processes || [],
      states: artifact.states || [],
      improved_states: artifact.improved_states || [],
      state_transitions: artifact.state_transitions || [],
      improved_transitions: artifact.improved_transitions || [],
      items: artifact.items || [],
      demand_processes: artifact.demand_processes || [],
      improvement_processes: artifact.improvement_processes || [],
      nested_processes: artifact.nested_processes || [],
      attrs: artifact.attrs || {},
      improvementStates: artifact.improvementStates || [],
      currentImprovementState: artifact.currentImprovementState || null,
      version: artifact.version || null,
      versions: artifact.versions || [],
      parent: artifact.parent || null
    };

    return {
      success: true,
      message: `Showing artifact: **${artifact.name}**`,
      cards: [{
        type: 'artifact-detail',
        artifactId: artifact.id,
        artifact: enhancedArtifact,
      }],
      data: { 
        action: 'show_artifact', 
        artifactName: artifact.name, 
        artifactId: artifact.id,
        found: true 
      },
      suggestions: [`/items ${artifact.name}`, `/list processes ${artifact.name}`],
    };
  }

  // Prime Model Command Handlers
  private async handlePrimeArtifact(args: string[], context: CommandContext): Promise<CommandResult> {
    const artifactQuery = args.join(' ').trim();

    if (!artifactQuery) {
      return {
        success: false,
        message: 'Prime artifact name is required',
        error: 'Usage: /prime-artifact "Artifact Name"',
        suggestions: ['/list artifacts'],
      };
    }

    try {
      // Dynamically import PrimeArtifactService
      const { PrimeArtifactService } = await import('../primeArtifactService');
      
      // Get all prime artifacts
      const primeArtifacts = await PrimeArtifactService.getArtifactsHierarchy();
      
      // Flatten hierarchy to search
      const flattenArtifacts = (artifacts: any[]): any[] => {
        return artifacts.reduce((acc, artifact) => {
          acc.push(artifact);
          if (artifact.children) {
            acc.push(...flattenArtifacts(artifact.children));
          }
          return acc;
        }, []);
      };
      
      const allPrimeArtifacts = flattenArtifacts(primeArtifacts);
      
      // Search for the artifact
      const artifact = allPrimeArtifacts.find((a: any) => 
        a.name?.toLowerCase().includes(artifactQuery.toLowerCase()) || 
        a.id?.toString().toLowerCase() === artifactQuery.toLowerCase()
      );

      if (!artifact) {
        return {
          success: false,
          message: `Prime artifact not found: "${artifactQuery}"`,
          error: `No prime artifact matching "${artifactQuery}"`,
          suggestions: ['/list artifacts', `/find artifact ${artifactQuery}`],
        };
      }

      // Fetch full artifact with states, transitions, processes
      const fullArtifact = await PrimeArtifactService.getById(artifact.id);

      return {
        success: true,
        message: `Showing Prime Artifact: **${fullArtifact.name}**`,
        cards: [{
          type: 'prime_artifact',
          artifactId: fullArtifact.id,
          primeArtifact: fullArtifact,
        }],
        data: { 
          action: 'show_prime_artifact', 
          artifactName: fullArtifact.name, 
          artifactId: fullArtifact.id,
        },
        suggestions: [
          `/prime-states ${fullArtifact.name}`, 
          `/prime-transitions ${fullArtifact.name}`,
          `/prime-processes ${fullArtifact.name}`
        ],
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to load prime artifact',
        error: error.message || 'An error occurred',
      };
    }
  }

  private async handlePrimeStatus(args: string[], context: CommandContext): Promise<CommandResult> {
    // Similar to handlePrimeArtifact but focused on status
    return this.handlePrimeArtifact(args, context);
  }

  private async handlePrimeStates(args: string[], context: CommandContext): Promise<CommandResult> {
    const artifactQuery = args.join(' ').trim();

    if (!artifactQuery) {
      return {
        success: false,
        message: 'Prime artifact name is required',
        error: 'Usage: /prime-states "Artifact Name"',
        suggestions: ['/prime-artifact'],
      };
    }

    try {
      const { PrimeArtifactService } = await import('../primeArtifactService');
      const primeArtifacts = await PrimeArtifactService.getArtifactsHierarchy();
      
      const flattenArtifacts = (artifacts: any[]): any[] => {
        return artifacts.reduce((acc, artifact) => {
          acc.push(artifact);
          if (artifact.children) {
            acc.push(...flattenArtifacts(artifact.children));
          }
          return acc;
        }, []);
      };
      
      const allPrimeArtifacts = flattenArtifacts(primeArtifacts);
      const artifact = allPrimeArtifacts.find((a: any) => 
        a.name?.toLowerCase().includes(artifactQuery.toLowerCase())
      );

      if (!artifact) {
        return {
          success: false,
          message: `Prime artifact not found: "${artifactQuery}"`,
          error: `No prime artifact matching "${artifactQuery}"`,
        };
      }

      const fullArtifact = await PrimeArtifactService.getById(artifact.id);

      return {
        success: true,
        message: `Showing states for: **${fullArtifact.name}**`,
        cards: [{
          type: 'prime_states',
          artifactId: fullArtifact.id,
          primeArtifact: fullArtifact,
        }],
        data: { 
          action: 'show_prime_states', 
          artifactId: fullArtifact.id,
        },
        suggestions: [
          `/prime-transitions ${fullArtifact.name}`,
          `/prime-artifact ${fullArtifact.name}`
        ],
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to load prime states',
        error: error.message || 'An error occurred',
      };
    }
  }

  private async handlePrimeTransitions(args: string[], context: CommandContext): Promise<CommandResult> {
    const artifactQuery = args.join(' ').trim();

    if (!artifactQuery) {
      return {
        success: false,
        message: 'Prime artifact name is required',
        error: 'Usage: /prime-transitions "Artifact Name"',
        suggestions: ['/prime-artifact'],
      };
    }

    try {
      const { PrimeArtifactService } = await import('../primeArtifactService');
      const primeArtifacts = await PrimeArtifactService.getArtifactsHierarchy();
      
      const flattenArtifacts = (artifacts: any[]): any[] => {
        return artifacts.reduce((acc, artifact) => {
          acc.push(artifact);
          if (artifact.children) {
            acc.push(...flattenArtifacts(artifact.children));
          }
          return acc;
        }, []);
      };
      
      const allPrimeArtifacts = flattenArtifacts(primeArtifacts);
      const artifact = allPrimeArtifacts.find((a: any) => 
        a.name?.toLowerCase().includes(artifactQuery.toLowerCase())
      );

      if (!artifact) {
        return {
          success: false,
          message: `Prime artifact not found: "${artifactQuery}"`,
          error: `No prime artifact matching "${artifactQuery}"`,
        };
      }

      const fullArtifact = await PrimeArtifactService.getById(artifact.id);

      return {
        success: true,
        message: `Showing transitions for: **${fullArtifact.name}**`,
        cards: [{
          type: 'prime_transitions',
          artifactId: fullArtifact.id,
          primeArtifact: fullArtifact,
        }],
        data: { 
          action: 'show_prime_transitions', 
          artifactId: fullArtifact.id,
        },
        suggestions: [
          `/prime-processes ${fullArtifact.name}`,
          `/prime-artifact ${fullArtifact.name}`
        ],
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to load prime transitions',
        error: error.message || 'An error occurred',
      };
    }
  }

  private async handlePrimeProcesses(args: string[], context: CommandContext): Promise<CommandResult> {
    const artifactQuery = args.join(' ').trim();

    if (!artifactQuery) {
      return {
        success: false,
        message: 'Prime artifact name is required',
        error: 'Usage: /prime-processes "Artifact Name"',
        suggestions: ['/prime-artifact'],
      };
    }

    try {
      const { PrimeArtifactService } = await import('../primeArtifactService');
      const primeArtifacts = await PrimeArtifactService.getArtifactsHierarchy();
      
      const flattenArtifacts = (artifacts: any[]): any[] => {
        return artifacts.reduce((acc, artifact) => {
          acc.push(artifact);
          if (artifact.children) {
            acc.push(...flattenArtifacts(artifact.children));
          }
          return acc;
        }, []);
      };
      
      const allPrimeArtifacts = flattenArtifacts(primeArtifacts);
      const artifact = allPrimeArtifacts.find((a: any) => 
        a.name?.toLowerCase().includes(artifactQuery.toLowerCase())
      );

      if (!artifact) {
        return {
          success: false,
          message: `Prime artifact not found: "${artifactQuery}"`,
          error: `No prime artifact matching "${artifactQuery}"`,
        };
      }

      const fullArtifact = await PrimeArtifactService.getById(artifact.id);

      return {
        success: true,
        message: `Showing processes for: **${fullArtifact.name}**`,
        cards: [{
          type: 'prime_processes',
          artifactId: fullArtifact.id,
          primeArtifact: fullArtifact,
        }],
        data: { 
          action: 'show_prime_processes', 
          artifactId: fullArtifact.id,
        },
        suggestions: [
          `/prime-states ${fullArtifact.name}`,
          `/prime-artifact ${fullArtifact.name}`
        ],
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to load prime processes',
        error: error.message || 'An error occurred',
      };
    }
  }
}
